import { WIDGET_PREVIEW_SIZE } from '../constants'

function parseElementStyle(props) {
  const { widget, zModifier, isRenderingInsideLayerTool, isRenderingInsideAceEditor } = props
  let widgetLayout = widget.layout || {}
  let widgetBaseStyle = widget.baseStyle ? JSON.parse(JSON.stringify(widget.baseStyle)) : {}
  let animationTypeMap = {}
  // In preparation for multiple animations, animation fill mode is converted to 'forwards' instead of 'both'.
  // This is because the 'both' fill mode does not work with multiple animations. This then requires us to set
  // the position of the actual widget to the starting point of the animation otherwise the widget will become visible
  // in the end position of the animation and not the starting position.
  if (widget.animations?.length && !isRenderingInsideAceEditor) {
    widget.animations.forEach(a => {
      if (
        (a.type !== 'transition' && animationTypeMap[a.type]) ||
        (a.type === 'transition' && animationTypeMap[a.targetStyle.name])
      ) {
        return
      }

      switch (true) {
        case a.type === 'move' || a.type === 'resize':
          animationTypeMap['move'] = true
          animationTypeMap['resize'] = true
          widgetLayout = {
            ...widgetLayout,
            x: a.animationStates[0].layout.x,
            y: a.animationStates[0].layout.y,
            width: a.animationStates[0].layout.width,
            height: a.animationStates[0].layout.height,
          }
          break
        case a.type === 'transition':
          animationTypeMap[a.targetStyle.name] = true
          widgetBaseStyle[a.targetStyle.name] = a.animationStates[0].transition.value
          break
        case a.type === 'rotation':
          animationTypeMap[a.type] = true
          widgetLayout = {
            ...widgetLayout,
            zRotation: a.animationStates[0].rotation.value,
          }
          widgetBaseStyle.horizontal = a.animationStates[0].horizontal.value
          widgetBaseStyle.vertical = a.animationStates[0].vertical.value
          break
      }
    })
  }

  // if inside ace editor, set to current base style, not starting point of animation
  if (isRenderingInsideAceEditor) {
    widgetBaseStyle = widget.baseStyle
  }
  const scale =
    isRenderingInsideLayerTool && WIDGET_PREVIEW_SIZE / Math.max(widgetLayout.width, widgetLayout.height) < 1
      ? `scale(${WIDGET_PREVIEW_SIZE / Math.max(widgetLayout.width, widgetLayout.height)})`
      : ''

  const st = {
    position: 'absolute',
    overflow: 'hidden',

    width: `${widgetLayout.width}px`,
    height: `${widgetLayout.height}px`,

    top: isRenderingInsideLayerTool ? '' : `${widgetLayout.y}px`,
    left: isRenderingInsideLayerTool ? '' : `${widgetLayout.x}px`,
    'z-index': widgetLayout.z + zModifier,

    background: widgetBaseStyle.background || 'transparent',
    color: widgetBaseStyle.color,
    // 0 values do not get rendered so we use percent for 0 values
    opacity: widgetBaseStyle.opacity === 0 ? '0%' : widgetBaseStyle.opacity || 1,
    // hide widget if visibility off and widget is inside template (does not hide widget from actual ad)
    // strict false check is necessary as isLayerVisible undefined should default to true (DO NOT CHANGE to falsy !widget.isLayerVisible check)
    // widget visibility by default should be true
    display: widget.isLayerVisible === false && isRenderingInsideAceEditor && !isRenderingInsideLayerTool ? 'none' : '',

    cursor: 'pointer',

    'border-width': `${widgetBaseStyle['border-width'] || 0}px`,
    'border-radius': `${widgetBaseStyle['border-radius'] * 100 || 0}px`,
    'border-style': widgetBaseStyle['border-style'],
    'border-color': widgetBaseStyle['border-color'],

    transform: isRenderingInsideLayerTool
      ? `${scale}`
      : `rotate(${widgetLayout.zRotation || 0}deg) rotateY(${widgetBaseStyle.horizontal ||
          0}deg) rotateX(${widgetBaseStyle.vertical || 0}deg)`,
  }

  return st
}

function withDivContainer(sourceElement, $h) {
  //context was bound to this
  const props = this.props
  //account for any native events the template editor is trying to apply (ie mouseover, contextmenu)
  const nativeOn = (this.data && this.data.nativeOn) || {}
  const nao = props.widget.addOns.find(e => (e.name = 'navigateToSceneOnClick'))

  // Generate hover event styling
  // Perry Todo - This whole hover-event-styling should be made into an Add-On that can be applied to any widget, see comment below
  const {
    hoverColorValue,
    hoverBackgroundValue,
    hoverBorderStyle,
    hoverBorderColor,
    hoverBorderWidth,
    hoverBorderRadius,
  } = props.widget.value.config
  let hoverColorCss = ''
  if (hoverColorValue) {
    switch (props.widget.value.config.svg) {
      case 'arrow_left':
      case 'arrow_right':
        hoverColorCss += `fill:${hoverColorValue};`
        break
      case 'chevron_left':
      case 'chevron_right':
        hoverColorCss += `stroke:${hoverColorValue};`
        break
    }
  }

  const hoverBackgroundCss = hoverBackgroundValue ? `background:${hoverBackgroundValue} !important;` : ''
  const hoverBorderCss =
    hoverBorderStyle != 'none' && hoverBorderWidth
      ? `border:${hoverBorderStyle} ${hoverBorderWidth}px ${hoverBorderColor} !important;`
      : ''
  const hoverBorderRadiusCss = `border-radius: ${hoverBorderRadius || 1}px !important`
  const hoverStyles = $h('style', {}, [
    `#${props.widgetId}:hover { ${hoverBackgroundCss} ${hoverBorderCss} ${hoverBorderRadiusCss}}
    #${props.widgetId}:hover > svg > * { ${hoverColorCss} }`,
  ])

  return $h(
    'div',
    {
      on: {
        ...nativeOn,
      },
      style: parseElementStyle(props),
      /*
      At time of writing, AddOns as a separate user interface in the template editor
      doesn't exist yet.  So, for the purposes of this widget, we'll treat the navigation addOn
      as a native aspect of this widget, ie assert there will always be an addOn named 'navigateToSceneOnClick'
      in the addOns list of this widget, rather than trying to programmatically determine if it is there or not
      Perry Todo - Addons should pass entire Vue data obj: https://vuejs.org/v2/guide/render-function.html#The-Data-Object-In-Depth
      */
      attrs: {
        id: props.widgetId,
        //Todo - When AddOns are properly implemented the following line should be removed,
        //instead there should be code (per above comments) that hydrates this Vue data obj with
        //ones from each configured addon
        'data-adacadonavigationwidget': nao.config.target,
        'widget-id': `widget${props.widget.id}`,
        class: props.animationClass,
      },
    },
    [hoverStyles, sourceElement]
  )
}

export default {
  functional: true,
  props: {
    widget: {
      type: Object,
      required: true,
    },
    animationClass: {
      type: String,
      default: '',
    },
    isRenderingInsideAceEditor: {
      type: Boolean,
      default: false,
    },
    isRenderingInsideLayerTool: {
      type: Boolean,
      default: false,
    },
    widgetId: {
      type: String,
      required: false,
    },
    zModifier: {
      type: Number,
      default: 0,
    },
  },
  render($h, context) {
    const widget = context.props.widget

    // This output should match those in the navigation widget inspector content component.
    let design
    switch (widget.value.config.svg) {
      case 'arrow_left': {
        design = $h('polygon', {
          attrs: {
            points: '0 10, 10 0, 10 20',
            stroke: 'none',
            fill: widget.baseStyle.color,
          },
        })
        break
      }
      case 'arrow_right': {
        design = $h('polygon', {
          attrs: {
            points: '0 0, 10 10, 0 20',
            stroke: 'none',
            fill: widget.baseStyle.color,
          },
        })
        break
      }
      case 'chevron_left': {
        design = $h('polyline', {
          attrs: {
            points: '9 1, 1 10, 9 19',
            stroke: widget.baseStyle.color,
            'stroke-width': 2,
            fill: 'none',
          },
        })
        break
      }
      case 'chevron_right': {
        design = $h('polyline', {
          attrs: {
            points: '1 1, 9 10, 1 19',
            stroke: widget.baseStyle.color,
            'stroke-width': 2,
            fill: 'none',
          },
        })
        break
      }
    }

    const navElement = $h(
      'svg',
      {
        attrs: {
          viewBox: '0 0, 10 20',
          preserveAspectRatio: 'none',
          width: '100%',
          height: '100%',
          version: '1.1',
          xmlns: 'http://www.w3.org/2000/svg',
        },
      },
      [design]
    )

    // wrap with div
    return withDivContainer.call(context, navElement, $h)
  },
}
